import React, { lazy, Suspense, useEffect, useState } from 'react';
import { Alert, Button, Empty, Modal, Spin } from 'antd';
import { FullscreenExitOutlined, FullscreenOutlined } from '@ant-design/icons';
import { getFileBlob } from '../utils/fileApi';
import StudentWatermark from './StudentWatermark';
import './PdfReader.css';

const PdfReader = lazy(() => import('./PdfReader'));

interface PreviewFile {
  file_url: string;
  file_name: string;
}

type Preview =
  | { kind: 'pdf' | 'image' | 'audio' | 'video'; url: string }
  | { kind: 'text'; text: string }
  | { kind: 'unsupported' };

// Normalize files served as application/octet-stream using their extension.
const mimeTypes: Record<string, string> = {
  pdf: 'application/pdf',
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif',
  webp: 'image/webp', svg: 'image/svg+xml', bmp: 'image/bmp', avif: 'image/avif',
  mp3: 'audio/mpeg', wav: 'audio/wav', ogg: 'audio/ogg', m4a: 'audio/mp4',
  mp4: 'video/mp4', webm: 'video/webm', mov: 'video/quicktime',
  txt: 'text/plain', csv: 'text/csv', md: 'text/plain', json: 'application/json',
  log: 'text/plain', xml: 'text/plain', html: 'text/plain', htm: 'text/plain',
};

const FilePreviewModal: React.FC<{
  file: PreviewFile | null;
  onClose: () => void;
}> = ({ file, onClose }) => {
  const [preview, setPreview] = useState<Preview | null>(null);
  const [error, setError] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const isPdf = preview?.kind === 'pdf' || file?.file_name.toLowerCase().endsWith('.pdf');

  useEffect(() => {
    setPreview(null);
    setError(false);
    setExpanded(true);
    if (!file) return;

    const controller = new AbortController();
    let objectUrl: string | undefined;

    const loadPreview = async () => {
      try {
        const blob = await getFileBlob(file.file_url, controller.signal);
        if (controller.signal.aborted) return;

        const extension = file.file_name.split('.').pop()?.toLowerCase() || '';
        const type = mimeTypes[extension] || blob.type.split(';')[0].toLowerCase();
        if (type.startsWith('text/') || type === 'application/json' || type === 'application/xml') {
          const text = await blob.text();
          if (!controller.signal.aborted) setPreview({ kind: 'text', text });
          return;
        }

        const kind = type === 'application/pdf' ? 'pdf'
          : type.startsWith('image/') ? 'image'
          : type.startsWith('audio/') ? 'audio'
          : type.startsWith('video/') ? 'video'
          : 'unsupported';

        if (kind === 'unsupported') {
          setPreview({ kind });
          return;
        }

        objectUrl = URL.createObjectURL(new Blob([blob], { type }));
        setPreview({ kind, url: objectUrl });
      } catch {
        if (!controller.signal.aborted) setError(true);
      }
    };

    void loadPreview();
    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [file]);

  return (
    <Modal
      title={
        <div className="file-preview-modal__title">
          <span className="file-preview-modal__name" title={file?.file_name}>{file?.file_name || 'File preview'}</span>
          {isPdf && (
            <Button
              icon={expanded ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
              aria-label={expanded ? 'Exit full screen' : 'Full screen'}
              title={expanded ? 'Exit full screen' : 'Full screen'}
              onClick={() => setExpanded(value => !value)}
            >
              <span className="file-preview-modal__fullscreen-label">{expanded ? 'Exit full screen' : 'Full screen'}</span>
            </Button>
          )}
        </div>
      }
      open={!!file}
      onCancel={onClose}
      footer={null}
      width={isPdf && expanded ? '100vw' : 'min(1200px, 95vw)'}
      style={{ top: isPdf && expanded ? 0 : 16, paddingBottom: isPdf ? 0 : undefined }}
      className={isPdf ? `file-preview-modal--pdf${expanded ? ' file-preview-modal--fullscreen' : ''}` : undefined}
    >
      {file && (
        <StudentWatermark style={isPdf ? { height: '100%' } : undefined}>
          <div style={isPdf ? { height: '100%' } : { minHeight: 200, maxHeight: '80vh', overflow: 'auto' }}>
            {error ? (
              <Alert type="error" showIcon message="Unable to preview this file. Please close the preview and try again." />
            ) : !preview ? (
              <div style={{ padding: 64, textAlign: 'center' }}>
                <Spin />
                <div style={{ marginTop: 12 }}>Loading preview...</div>
              </div>
            ) : preview.kind === 'unsupported' ? (
              <Empty description="Preview is not available for this file format." />
            ) : preview.kind === 'text' ? (
              <pre style={{ margin: 0, padding: 16, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{preview.text}</pre>
            ) : preview.kind === 'image' ? (
              <img src={preview.url} alt={file.file_name} onError={() => setError(true)} style={{ display: 'block', maxWidth: '100%', maxHeight: '75vh', margin: '0 auto', objectFit: 'contain' }} />
            ) : preview.kind === 'audio' ? (
              <audio src={preview.url} controls onError={() => setError(true)} style={{ width: '100%' }} />
            ) : preview.kind === 'video' ? (
              <video src={preview.url} controls onError={() => setError(true)} style={{ width: '100%', maxHeight: '75vh' }} />
            ) : (
              <Suspense fallback={<div className="pdf-reader__message"><Spin /><span>Loading PDF...</span></div>}>
                <PdfReader url={preview.url} fileName={file.file_name} />
              </Suspense>
            )}
          </div>
        </StudentWatermark>
      )}
    </Modal>
  );
};

export default FilePreviewModal;
